/**
 * Adjusts the given date to the nearest working day.
 * For a start date, weekends are moved forward (Saturday → Monday, Sunday → Monday).
 * For an end date, weekends are moved backward (Saturday → Friday, Sunday → Friday).
 *
 * @param {Date} date - The date to adjust.
 * @param {"start" | "end"} type - Specify if adjusting for a start date or an end date.
 * @returns {Date} - The adjusted working day.
 */
export function adjustToWorkingDay(date, type) {
    const day = date.getDay();
    if (type === "start") {
      if (day === 6) {
        date.setDate(date.getDate() + 2);
      } else if (day === 0) {
        date.setDate(date.getDate() + 1);
      }
    }
    if (type === "end") {
      if (day === 6) {
        date.setDate(date.getDate() - 1);
      } else if (day === 0) {
        date.setDate(date.getDate() - 2);
      }
    }
    return date;
  }
  
  /**
   * Returns the current fiscal year's start and end dates.
   * The fiscal year is defined as April 1 to March 31.
   * The fiscal end date is adjusted to the last working day.
   *
   * @returns {{ fyStart: Date, fyEnd: Date }} - The fiscal year start and end dates.
   */
  export function getCurrentFiscalYearDates() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-indexed: March is 2
    const day = today.getDate();
    let fyStart, fyEnd;
  
    // If today is on or after March 28, current FY starts this year's March 28
    if (month > 2 || (month === 2 && day >= 28)) {
      fyStart = new Date(year, 2, 28);
      fyEnd = new Date(year + 1, 2, 27);
    } else {
      // Before March 28, FY started last year's March 28 and ends this year's March 27
      fyStart = new Date(year - 1, 2, 28);
      fyEnd = new Date(year, 2, 27);
    }
  
    return { fyStart, fyEnd };
  }


  /**
 * Formats a Date object into a string (YYYY-MM-DD).
 *
 * @param {Date} date - The date to format.
 * @returns {string} - The formatted date string.
 */
export function formatDate(date) {
  const year = date.getFullYear();
  // getMonth returns 0-indexed month so we add 1.
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Returns the previous month's start and end dates adjusted to working days.
 * - Start date: First day of the previous month (adjusted forward if it's a weekend)
 * - End date: Last day of the previous month (adjusted backward if it's a weekend)
 *
 * @returns {{ startDate: Date, endDate: Date }} - The adjusted previous month dates.
 */
export function getPreviousMonthWorkingDates() {
  const today = new Date();
  const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1;

  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  return { startDate, endDate };
}


  /**
 * Adjusts the user-selected start and end dates to working days.
 * This function ensures:
 *   - The start date is moved forward to the next working day if it falls on a weekend.
 *   - The end date is moved backward to the previous working day if it falls on a weekend.
 *   - Both dates are clamped within the current fiscal year.
 *
 * @param {Date|string} selectedStart - The user-selected start date.
 * @param {Date|string} selectedEnd - The user-selected end date.
 * @returns {{ adjustedStart: Date, adjustedEnd: Date }} - The adjusted dates.
 */
export function adjustSelectedDateRange(selectedStart, selectedEnd) {
  let adjustedStart = new Date(selectedStart);
  let adjustedEnd = new Date(selectedEnd);

  // Clamp within fiscal year
  const { fyStart, fyEnd } = getCurrentFiscalYearDates();
  if (adjustedStart < fyStart) adjustedStart = new Date(fyStart);
  if (adjustedEnd > fyEnd) adjustedEnd = new Date(fyEnd);

  return { adjustedStart, adjustedEnd };
}
  
  /**
   * Returns a date range (start and end) that adjusts for working days
   * and is clamped within the current fiscal year.
   * The end date is automatically T-1 (yesterday) adjusted to a working day.
   * The start date is computed as (endDate - (rangeDays - 1)) and adjusted forward if needed.
   *
   * @param {number} rangeDays - The number of days in the range (default is 7).
   * @returns {{ startDate: Date, endDate: Date }} - The working start and end dates.
   */
  export function getWorkingDateRange(rangeDays = 7) {
    const today = new Date();
    const endDate = new Date(today);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (rangeDays- 1 ));
  
    const { fyStart, fyEnd } = getCurrentFiscalYearDates();
    if (startDate < fyStart) startDate.setTime(fyStart.getTime());
    if (endDate > fyEnd) endDate.setTime(fyEnd.getTime());
  
    return { startDate, endDate };
  }
  

  // past 30 days no limit
export const getPast30DaysRange = () => {
  const endDate = new Date();
  endDate.setDate(endDate.getDate() - 1);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 29);
  return { startDate, endDate };
};

// current financial year
export function getCurrentFinancialYearRange() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // JS months are 0-indexed

  const startYear = month >= 4 ? year : year - 1;
  const endYear = startYear + 1;

  const formatDate = (day, monthStr, year) => `${day}-${monthStr}-${year}`;

  const startDate = formatDate("01", "Apr", startYear);
  const endDate = formatDate("31", "Mar", endYear);

  return `${startDate} - ${endDate}`;
}

// today data
// utils/dateFormat.js (or any utility file)
export const formatDateToday = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// format month 
export const formatDateDay = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "long" }); 
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

// date range format for specific date ranges
// utils/dateRangeUtils.js

export const getQuickRangeDates = (type) => {
  const today = new Date();

  const formatDate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;

  let start, end = today;

  switch (type) {
    case "today":
      start = today;
      break;

    case "last_7_days":
      start = new Date(today);
      start.setDate(start.getDate() - 7);
      break;

    case "last_15_days":
      start = new Date(today);
      start.setDate(start.getDate() - 15);
      break;

    case "last_30_days":
      start = new Date(today);
      start.setDate(start.getDate() - 30);
      break;

    case "this_month":
      // Financial year start → 28 March
      start = new Date(today.getFullYear(), 2, 28);
      break;

    default:
      start = today;
  }

  return {
    start,
    end,
    formattedStart: formatDate(start),
    formattedEnd: formatDate(end),
  };
};


